import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useUser, useLoading } from "@/contexts";
import { PublicPhoto, userService } from "@/services";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { PhotoSlider, EditImageModal, Message, Tooltip } from "@/components";
import { db } from "@/services/config";
import { getAge } from "@/utils";

import "./Profile.css";

const ProfileScreen: React.FC = () => {
  const navigate = useNavigate();
  const { refreshUser, user, userData } = useUser();
  const { withLoading } = useLoading();

  const publicInputRef = useRef<HTMLInputElement>(null);

  const [pendingPhotoPreview, setPendingPhotoPreview] = useState<string | null>(
    null,
  );
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null);
  const [showEditImageModal, setShowEditImageModal] = useState(false);
  const [isPhotoValid, setIsPhotoValid] = useState(false);

  const [publicPhotos, setPublicPhotos] = useState<PublicPhoto[]>([]);

  const [isPhotoLoading, setIsPhotoLoading] = useState(false);

  const age = getAge(userData?.birthYear);

  const profileImageUrl =
    typeof userData?.profileImage === "string"
      ? userData.profileImage
      : userData?.profileImage?.url;

  const isProfilePhotoNotApproved =
    userData?.profileImage &&
    typeof userData.profileImage === "object" &&
    userData.profileImage.approved === false;

  const handleProfilePhotoUpload = async (file: File | null) => {
    if (!user || !file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      alert("Por favor, selecione uma imagem JPEG ou PNG.");
      return;
    }

    try {
      setIsPhotoLoading(true);

      await withLoading(() => userService.uploadProfilePhoto(user.uid, file));

      setPendingPhoto(null);
      setPendingPhotoPreview(null);
      setShowEditImageModal(false);

      await refreshUser();
    } catch (error) {
      console.error("Erro ao atualizar foto de perfil:", error);
    } finally {
      setIsPhotoLoading(false);
    }
  };

  const handlePublicPhotosUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    if (!user || !files || files.length === 0) return;

    const file = files[0];

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      alert("Por favor, selecione uma imagem JPEG ou PNG.");
      return;
    }

    try {
      setIsPhotoLoading(true);

      await withLoading(() => userService.addPublicPhoto(user.uid, file));

      await refreshUser();
    } catch (error) {
      console.error("Erro ao enviar foto pública:", error);
    } finally {
      setIsPhotoLoading(false);
      if (publicInputRef.current) publicInputRef.current.value = "";
    }
  };

  const handleDeleteProfilePhoto = async (url: string) => {
    if (!user || !userData?.profileImage) return;

    const confirmDelete = window.confirm("Deseja excluir sua foto de perfil?");

    if (!confirmDelete) return;

    try {
      setIsPhotoLoading(true);

      const storagePath = userService.extractStoragePath(url, user.uid);

      if (!storagePath) return;

      await withLoading(() =>
        userService.deleteProfilePhoto(user.uid, storagePath),
      );

      const userRef = doc(db, "users", user.uid);

      await updateDoc(userRef, {
        profileImage: null,
        updatedAt: serverTimestamp(),
      });

      await refreshUser();
    } catch {
      console.log("Erro ao excluir foto.");
    } finally {
      setIsPhotoLoading(false);
    }
  };

  const handleDeletePublicPhoto = async (url: string) => {
    if (!user) return;

    const confirmClose = window.confirm("Deseja mesmo excluir esta foto?");

    if (!confirmClose) return;

    const storagePath = userService.extractStoragePath(url, user.uid);

    if (!storagePath) {
      console.log("Não foi possível localizar a foto para excluir.");
      return;
    }

    setIsPhotoLoading(true);

    try {
      const updated = publicPhotos.filter((item) => item.url !== url);

      await withLoading(() =>
        userService.deletePublicPhoto(user.uid, storagePath, updated),
      );

      setPublicPhotos(updated);

      await refreshUser();
    } catch (err) {
      console.log("Erro ao excluir foto.");
    } finally {
      setIsPhotoLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.publicPhotos) {
      const normalizedPhotos = userData.publicPhotos.map((photo: any) => {
        if (typeof photo === "string") {
          return {
            url: photo,
            approved: true,
            createdAt: null,
          };
        }
        return photo;
      });

      setPublicPhotos(normalizedPhotos);
    } else {
      setPublicPhotos([]);
    }
  }, [userData]);

  return (
    <div className="profile-page container">
      {userData?.awaitingApproval && (
        <Message
          title="Dica de Aprovação"
          description="Completar suas fotos e informações de contato pode acelerar a
          aprovação e ganhar mais destaque."
          type="warn"
        />
      )}

      <div className="profile-header">
        <div className="profile-image-section">
          <div className="profile-image-container profile-edit-overlay">
            {pendingPhotoPreview || profileImageUrl ? (
              <>
                <img
                  src={pendingPhotoPreview || profileImageUrl || undefined}
                  alt="Profile"
                  className="profile-image"
                />

                {isProfilePhotoNotApproved && (
                  <Tooltip text="Esta foto está em análise e será liberada em breve.">
                    <div className="private-badge">🔒</div>
                  </Tooltip>
                )}
              </>
            ) : (
              <div className="profile-placeholder-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="120"
                  height="120"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
            )}

            <div className="profile-edit-actions-overlay">
              <button
                type="button"
                className="btn-icon-small"
                onClick={() => setShowEditImageModal(true)}
                title="Alterar Foto"
                disabled={isPhotoLoading}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 20h9"></path>
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                </svg>
              </button>
              {(pendingPhotoPreview || profileImageUrl) && (
                <button
                  type="button"
                  className="btn-icon-small"
                  onClick={() => {
                    if (profileImageUrl)
                      handleDeleteProfilePhoto(profileImageUrl);
                  }}
                  title="Excluir Foto"
                  disabled={isPhotoLoading}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="profile-info-section">
          <div className="profile-main-info">
            <div className="user-identity">
              <h1>
                {userData?.username || "Usuário"},{" "}
                <span className="user-age">{age}</span>
                {userData?.isVerified && (
                  <span className="pill pill-verified">✔&#xFE0E;</span>
                )}
              </h1>
              <div className="user-location">
                {userData?.city || ""}, {userData?.state || ""}
              </div>
            </div>

            <div className="profile-actions">
              <button
                className="btn-secondary btn-icon"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/editar-perfil");
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
                Editar informações
              </button>
              <button
                className="btn-secondary btn-icon"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/configuracoes");
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
                Configurações e atividade
              </button>
            </div>
          </div>

          <div className="profile-tagline">
            <p>{userData?.bioTitle || "Sem título"}</p>
          </div>
        </div>
      </div>

      <div className="profile-content-grid">
        <aside className="profile-sidebar">
          <div className="info-group">
            <h3>Informações Pessoais</h3>
            <div className="tags-container">
              <span className="profile-tag">
                ❤️ {userData?.maritalStatus || "-"}
              </span>
              <span className="profile-tag">
                👶 {userData?.children || "-"}
              </span>
              <span className="profile-tag">🔍 {userData?.seeking || "-"}</span>
            </div>
          </div>

          <div className="info-group">
            <h3>Aparência</h3>
            <div className="tags-container">
              <span className="profile-tag">
                📏 {userData?.height ? `${userData.height}` : "-"}
              </span>
              <span className="profile-tag">
                💪 {userData?.bodyType || "-"}
              </span>
              <span className="profile-tag">
                🧔🏽‍♂️ {userData?.skinTone || "-"}
              </span>
              <span className="profile-tag">
                💇 {userData?.hairColor || "-"}
              </span>
              <span className="profile-tag">
                👁️ {userData?.eyeColor || "-"}
              </span>
            </div>
          </div>

          <div className="info-group">
            <h3>Estilo de Vida</h3>
            <div className="tags-container">
              <span className="profile-tag">🚬 {userData?.smoking || "-"}</span>
              <span className="profile-tag">
                🍷 {userData?.drinking || "-"}
              </span>
            </div>
          </div>

          <div className="info-group">
            <h3>Carreira e Finanças</h3>
            <div className="tags-container">
              <span className="profile-tag">
                🎓 {userData?.education || "-"}
              </span>
              <span className="profile-tag">
                � {userData?.profession || "-"}
              </span>
            </div>
          </div>
        </aside>

        <main className="profile-main-content">
          <section className="photos-section">
            <div className="content-section-header">
              <h2>Fotos Públicas</h2>
              <span className="photo-count">{publicPhotos.length}/12</span>
            </div>

            <PhotoSlider
              photos={publicPhotos}
              isLoading={isPhotoLoading}
              onAddClick={() => publicInputRef.current?.click()}
              showDelete={true}
              onDeletePhoto={(url) => handleDeletePublicPhoto(url)}
            />

            <input
              type="file"
              multiple
              accept="image/jpeg,image/png"
              style={{ display: "none" }}
              ref={publicInputRef}
              onChange={handlePublicPhotosUpload}
            />
          </section>

          <section className="bio-section">
            <div className="content-section-header">
              <h2>Sobre Mim</h2>
            </div>
            <p className="bio-text">
              {userData?.bioDescription || "Sem descrição."}
            </p>
          </section>
        </main>
      </div>

      <EditImageModal
        isOpen={showEditImageModal}
        onClose={() => setShowEditImageModal(false)}
        pendingPhoto={pendingPhoto}
        onImageChange={(e) => handleProfilePhotoUpload(e)}
        onValidationChange={setIsPhotoValid}
      />
    </div>
  );
};

export default ProfileScreen;
